import { useState, useEffect, useMemo } from 'react';
import { EditIcon, TrashIcon, RefreshCwIcon } from 'lucide-react';
import { deltaToHtml, type QuillDelta } from '@/components/custom/quill-editor';
import { DataTable, type Column } from '@/components/custom/data-table';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { formSubmissionsApi } from '@/lib/api/form-submissions';
import type { Form, CollectionColumn, ResourceOperation } from '@/types/entities';
import { Badge } from '@/components/ui/badge';
import { RecordEditorPage } from '@/components/shared/record-editor-page';

export type FormTenant = {
  id: number;
  slug: string;
  name: string;
  websiteUrl: string;
};

type FormSubmissionsPanelProps = {
  tenant: FormTenant;
  form: Form;
  onFormChange?: () => void;
  editorMode?: 'create' | 'edit' | null;
  editingSubmissionId?: number | null;
  onCreateRoute?: () => void;
  onEditRoute?: (submissionId: number) => void;
  onExitEditorRoute?: () => void;
  enforceAccess?: boolean;
};

interface SubmissionItem {
  id: number;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

export function FormSubmissionsPanel({
  tenant,
  form,
  onFormChange,
  editorMode = null,
  editingSubmissionId = null,
  onCreateRoute,
  onEditRoute,
  onExitEditorRoute,
  enforceAccess = false,
}: FormSubmissionsPanelProps) {
  const [data, setData] = useState<SubmissionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SubmissionItem | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const allowedOps = useMemo(() => {
    const configuredOps = form.details.ops?.length ? form.details.ops : ALL_OPERATIONS;
    const normalizedOps = Array.from(new Set<ResourceOperation>(configuredOps));
    return ALL_OPERATIONS.filter((operation) => normalizedOps.includes(operation));
  }, [form.details.ops]);

  const canCreate = !enforceAccess || allowedOps.includes('create');
  const canUpdate = !enforceAccess || allowedOps.includes('update');
  const canDelete = !enforceAccess || allowedOps.includes('delete');

  const editingItem = useMemo(() => {
    if (!editingSubmissionId) {
      return undefined;
    }
    return data.find((item) => item.id === editingSubmissionId);
  }, [data, editingSubmissionId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const items = await formSubmissionsApi.getAllForForm(tenant.slug, form.name);
      setData(items);
    } catch (err) {
      console.error('Failed to load form submissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenant.slug, form.name]);

  const handleCreate = () => {
    if (!canCreate) {
      return;
    }
    onCreateRoute?.();
  };

  const handleEdit = (item: SubmissionItem) => {
    if (!canUpdate) {
      return;
    }
    onEditRoute?.(item.id);
  };

  const handleDelete = (item: SubmissionItem) => {
    if (!canDelete) {
      return;
    }
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    setIsSaving(true);
    try {
      if (editorMode === 'edit' && editingSubmissionId) {
        if (!canUpdate) {
          throw new Error('Update is disabled for this form');
        }
        await formSubmissionsApi.update(tenant.slug, form.name, editingSubmissionId, formData);
      } else {
        if (!canCreate) {
          throw new Error('Create is disabled for this form');
        }
        await formSubmissionsApi.create(tenant.slug, form.name, formData);
      }
      setError(null);
      await fetchData();
      onFormChange?.();
      onExitEditorRoute?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    if (!canDelete) {
      setError('Delete is disabled for this form');
      return;
    }

    try {
      await formSubmissionsApi.delete(tenant.slug, form.name, itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(undefined);
      await fetchData();
      onFormChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data');
    }
  };

  const formatCellValue = (value: unknown, column: CollectionColumn) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;

    if (column.type === 'boolean') {
      return value ? (
        <Badge variant="default">Yes</Badge>
      ) : (
        <Badge variant="secondary">No</Badge>
      );
    }

    if (column.type === 'richtext') {
      const html = deltaToHtml(value as QuillDelta | string);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const textContent = tempDiv.textContent || '';
      return (
        <span className="line-clamp-2 text-sm">
          {textContent.substring(0, 100)}
          {textContent.length > 100 && '...'}
        </span>
      );
    }

    if (column.type === 'datetime') {
      return new Date(String(value)).toLocaleString();
    }

    return String(value);
  };

  const getSortableValue = (item: SubmissionItem, column: CollectionColumn) => {
    const value = item[column.name];
    if (value === null || value === undefined) {
      return null;
    }

    if (column.type === 'datetime') {
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (column.type === 'number') {
      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    }

    if (column.type === 'boolean') {
      return Boolean(value);
    }

    return String(value);
  };

  const columns: Column<SubmissionItem>[] = [
    {
      key: 'id',
      header: 'ID',
      className: 'w-20',
      cell: (item) => <span className="text-muted-foreground">#{item.id}</span>,
      sortable: true,
      sortValue: (item) => item.id,
    },
    ...form.details.columns.map<Column<SubmissionItem>>((col) => ({
      key: col.name,
      header: col.label,
      cell: (item) => formatCellValue(item[col.name], col),
      sortable: true,
      sortValue: (item) => getSortableValue(item, col),
    })),
    {
      key: 'created_at',
      header: 'Created',
      className: 'w-32',
      cell: (item) => <span className="text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</span>,
      sortable: true,
      sortValue: (item) => new Date(item.created_at),
    },
    ...(canUpdate || canDelete
      ? [{
          key: 'actions',
          header: 'Actions',
          className: 'w-24',
          cell: (item: SubmissionItem) => (
            <div className="flex items-center gap-1">
              {canUpdate ? (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)}>
                  <EditIcon className="size-3" />
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => handleDelete(item)}
                >
                  <TrashIcon className="size-3" />
                </Button>
              ) : null}
            </div>
          ),
        }]
      : []),
  ];

  const searchKeys = useMemo(
    () => ['id', 'created_at', ...form.details.columns.map((col) => col.name)] as (keyof SubmissionItem)[],
    [form.details.columns]
  );

  if (editorMode === 'edit' && !isLoading && !editingItem) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Submission not found</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode === 'create' && !canCreate) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Create is disabled for this form</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode === 'edit' && !canUpdate) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Update is disabled for this form</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode) {
    return (
      <RecordEditorPage
        key={`${editorMode}-${editingSubmissionId ?? 'new'}-${form.id}`}
        mode={editorMode}
        resourceName={form.display_name}
        columns={form.details.columns}
        tenantSlug={tenant.slug}
        initialData={editorMode === 'edit' ? editingItem : undefined}
        onSave={handleSave}
        onBack={() => onExitEditorRoute?.()}
        isSaving={isSaving}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center gap-2">
        <Badge variant="secondary" className="font-mono">
          {form.name}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {data.length} {data.length === 1 ? 'submission' : 'submissions'}
        </span>
      </div>

      {error && <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable
          data={data}
          columns={columns}
          searchKeys={searchKeys}
          searchPlaceholder="Search submissions..."
          emptyMessage="No submissions yet"
          isLoading={isLoading}
          onCreate={canCreate ? handleCreate : undefined}
          createLabel="Add Submission"
          actions={
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
              <RefreshCwIcon className="size-4" />
              Refresh
            </Button>
          }
        />
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Submission"
        description={`Are you sure you want to delete submission #${itemToDelete?.id}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </div>
  );
}
